import _ from 'lodash';

export const getAttendanceStatusLabelMap = (stringBundle) => {
    return {
        ATTENDED: stringBundle.statusAttended(),
        NOT_MARKED: stringBundle.statusNotMarked(),
        NO_SHOW: stringBundle.statusNoShow(),
        PARTIALLY_ATTENDED: stringBundle.statusPartiallyAttended(),
    };
};

export const isSessionAttendanceModified = (sessionAttendance, originalAttendanceRecord) => {
    const originalSessionAttendance = _.find(
        originalAttendanceRecord.deliverySessionAttendanceRecords,
        (record) => {
            return _.eq(sessionAttendance.deliverySessionId, record.deliverySessionId);
        },
        0,
    );

    return !_.isEqual(originalSessionAttendance, sessionAttendance);
};

export const submitAttendance = ({
    markAttendanceFn,
    attendanceState,
    eventHandlers,
    attendanceRefetcher,
    stringBundle,
}) => {
    const sessionAttendancesWithUpdateBySessionId = buildSessionAttendanceInputs(
        attendanceState.learnersWithSessionAttendanceChanges,
        attendanceState.attendanceRecordList,
        attendanceState.attendanceRecordListReadOnly,
    );

    // First, organize by learner ID to split into batches
    const learnerMap = new Map();
    _.forEach(sessionAttendancesWithUpdateBySessionId, (value) => {
        _.forEach(value, (attendance) => {
            if (!learnerMap.has(attendance.learner)) {
                learnerMap.set(attendance.learner, true);
            }
        });
    });

    // Split learners into batches
    const BATCH_LENGTH = 25;
    const learnerBatches = [];
    const learners = Array.from(learnerMap.keys());
    for (let i = 0; i < learners.length; i += BATCH_LENGTH) {
        learnerBatches.push(new Set(learners.slice(i, i + BATCH_LENGTH)));
    }

    // Create session-specific inputs for each batch of learners
    const batchedInputs = learnerBatches.map((learnerBatch) => {
        return _.map(sessionAttendancesWithUpdateBySessionId, (attendances, sessionId) => ({
            deliverySessionId: sessionId,
            learnerAttendance: attendances
                .filter((attendance) => learnerBatch.has(attendance.learner))
                .map((attendance) => ({
                    learner: attendance.learner,
                    attendanceDurationPercentage: attendance.attendanceDurationPercentage,
                })),
        }));
    });

    const onMarkingAttendanceSuccess = (_values) => {
        eventHandlers.onSubmitAttendanceChangeSuccess();
        attendanceRefetcher();
        eventHandlers.onSuccess(
            stringBundle.markAttendanceSuccessfulHeader(),
            stringBundle.markAttendanceSuccessfulText(learnerMap.size),
        );
    };

    const onMarkingAttendanceError = (_errors) => {
        eventHandlers.onSubmitAttendanceChangeError();
        eventHandlers.onError(
            stringBundle.markAttendanceErrorHeader(),
            stringBundle.markAttendanceErrorText(),
        );
    };

    eventHandlers.onSubmitAttendanceChange();

    // Process all batches
    if (batchedInputs.length === 1) {
        markAttendanceFn(batchedInputs[0], onMarkingAttendanceSuccess, onMarkingAttendanceError);
    } else {
        let completedBatches = 0;
        for (const batch of batchedInputs) {
            markAttendanceFn(
                batch,
                () => {
                    completedBatches++;
                    if (completedBatches === batchedInputs.length) {
                        onMarkingAttendanceSuccess();
                    }
                },
                onMarkingAttendanceError,
            );
        }
    }
};

const buildSessionAttendanceInputs = (
    learnersWithChange,
    attendanceRecordList,
    unmodifiedAttendanceRecordList,
) => {
    const unmodifiedAttendanceRecordsByUserId = _.reduce(
        unmodifiedAttendanceRecordList,
        (resultMap, attendanceRecordRO) => {
            resultMap.set(attendanceRecordRO.learner.id, attendanceRecordRO);
            return resultMap;
        },
        new Map(),
    );

    const attendanceRecordsWithChange = _.filter(attendanceRecordList, (attendanceRecord) =>
        learnersWithChange.has(attendanceRecord.learner.id),
    );
    const modifiedSessionAttendanceList = _.reduce(
        attendanceRecordsWithChange,
        (result, attendanceRecord) => {
            _.forEach(attendanceRecord.deliverySessionAttendanceRecords, (sessionAttendance) => {
                if (
                    isSessionAttendanceModified(
                        sessionAttendance,
                        unmodifiedAttendanceRecordsByUserId.get(attendanceRecord.learner.id),
                    )
                ) {
                    result.push({
                        deliverySessionId: sessionAttendance.deliverySessionId,
                        learner: attendanceRecord.learner.id,
                        attendanceDurationPercentage:
                            sessionAttendance.attendanceDurationPercentage,
                        attendanceStatus: sessionAttendance.attendanceStatus,
                    });
                }
            });
            return result;
        },
        [],
    );

    return _.groupBy(modifiedSessionAttendanceList, (item) => item.deliverySessionId);
};
